import { ModalOverlayLoading } from '@monorepo/shared/componentsV2/Loading';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import { useLogEntryFields } from '@monorepo/shared/hooks/logs/useLogFields';
import { useCurrentUser } from '@monorepo/shared/hooks/useCurrentUser';
import { fillHeightAndScrollable } from '@monorepo/shared/styles/layout';
import React, {
  useEffect,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from 'react';
import styled from 'styled-components';
import {
  useEditableLogEntryTableColumns,
  useLogEntryTableColumns,
} from '../../LogEntryTable/useLogEntryTableColumns';
import { usePersistClearedFormFieldValues } from '../hooks/usePersistClearedFormFieldValues';
import { useSyncEditedLogEntryIds } from '../hooks/useSyncEditedLogEntryIds';
import { type SelectedFile } from '../hooks/useUploadedFiles';
import { CurrentEntrySubtitle, CurrentEntrySubtitleText } from '../styled';

const SubheaderWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 39px;
  padding: 0 1rem;
  margin: -1rem -1rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayeee};
`;

const StyledCurrentEntrySubtitle = styled(CurrentEntrySubtitle)`
  margin-top: 0;
`;

const StyledTable = styled(Table)`
  ${fillHeightAndScrollable}
  width: calc(100% + 32px);
  margin: -16px -1rem;

  &:focus {
    outline: none;
  }
` as typeof Table;

export function LogEntriesTableReviewModalPage({
  selectedFile,
  logId,
  organizationId,
  projectId,
  isReadOnly,
  isSubmitting,
  forceShowErrors,
  setEditedLogEntryIds,
  setLogEntries,
  logEntryIDsToDelete = [],
  setLogEntryIDsToDelete,
}: {
  selectedFile?: SelectedFile;
  logId: string;
  organizationId: string;
  projectId: string;
  isReadOnly: boolean;
  isSubmitting: boolean;
  forceShowErrors?: boolean;
  setEditedLogEntryIds?: Dispatch<SetStateAction<Set<string>>>;
  setLogEntries?: (logEntries: LogEntry[]) => void;
  logEntryIDsToDelete: string[];
  setLogEntryIDsToDelete: (logEntries: string[]) => void;
}) {
  const logEntries = selectedFile?.logEntries;
  const filename = selectedFile?.type === 'UploadedFile' && selectedFile.name;
  const { logFields } = useLogEntryFields({
    organizationId,
    logId,
  });
  const allLogEntryIds = useMemo(
    () => logEntries?.map((entry) => entry.id) ?? [],
    [logEntries],
  );
  useSyncEditedLogEntryIds(setEditedLogEntryIds, allLogEntryIds);
  usePersistClearedFormFieldValues();
  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();
  const { currentUser } = useCurrentUser();

  // Reaching this page confirms every entry the user didn't already confirm by
  // visiting it on the edit page.
  useEffect(() => {
    if (isReadOnly) return;
    if (!setLogEntries || !currentUser?.id || !logEntries) return;
    if (logEntries.every((entry) => entry.reviewedBy)) return;

    setLogEntries(
      logEntries.map((entry) =>
        entry.reviewedBy ? entry : { ...entry, reviewedBy: currentUser.id },
      ),
    );
  }, [currentUser?.id, isReadOnly, logEntries, setLogEntries]);

  const columns = useLogEntryTableColumns({
    logEntries,
    logEntryFields: logFields || [],
    isBulkActionsModeOn: false,
    isFieldDescriptionsShown: false,
  });
  const editableColumns = useEditableLogEntryTableColumns({
    organizationId,
    projectId,
    logEntryFields: logFields || [],
    forceShowErrors,
    logEntryIDsToDelete,
    setLogEntryIDsToDelete,
  });

  return (
    <>
      {isSubmitting && <ModalOverlayLoading />}
      <SkipLink linkText="Skip to the modal footer" />
      {filename && (
        <SubheaderWrapper>
          <StyledCurrentEntrySubtitle>
            <CurrentEntrySubtitleText>
              Review entries for {filename}
            </CurrentEntrySubtitleText>
          </StyledCurrentEntrySubtitle>
        </SubheaderWrapper>
      )}
      <StyledTable
        ariaLabel="All Generated Log entries"
        canRowsBeSelected={false}
        columns={isReadOnly ? columns : editableColumns}
        getRowProps={(row) => ({
          $isSelected: logEntryIDsToDelete.includes(row.id),
        })}
        isRowSelectionDisabled
        rows={logEntries || []}
        isCompact
        scrollToBottomOnRowAdded
      />
      <SkipLinkTarget />
    </>
  );
}
